<template>
  <v-container fluid>
    <div class="welcome-wrapper">
      <v-form id="welcome-form" @submit.prevent="onSubmit" class="welcome px-5 py-3">
        <v-img max-width="250" v-show="$vuetify.breakpoint.mdAndUp" src="/img/veroFresh.png"/>
        <p class="greeting-text">{{ $t('welcome_page_ui.greeting') }}</p>

        <password-change-form
          ref="password-change-form"
          layout="vertical"
          :password.sync="password"/>

        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col cols="12" sm="6">
            <div class="welcome-input">
              <v-text-field
                id="user-first-name"
                :label="$t('first_name')"
                v-model="firstName"
                :error-messages="firstNameErrors"
                solo/>
            </div>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="welcome-input">
              <v-text-field
                id="user-last-name"
                :label="$t('last_name')"
                v-model="lastName"
                :error-messages="lastNameErrors"
                solo/>
            </div>
          </v-col>
        </v-row>

        <div class="welcome-input">
          <v-text-field
            id="user-phone-number"
            :label="$t('phone_number')"
            v-model="phone"
            :error-messages="phoneErrors"
            solo/>
        </div>

        <div class="welcome-input">
          <v-text-field
            id="user-title"
            :label="$t('job_title')"
            v-model="title"
            :error-messages="titleErrors"
            solo/>
        </div>

        <div class="department welcome-input">
          <v-radio-group v-model="department_1" :error-messages="departmentErrors">
            <v-radio
              v-for="value in departmentOptions()"
              :key="value"
              :value="value"
              :label="$t(value)">
            </v-radio>
          </v-radio-group>
          <div v-if="department_1 ==='other'">
            <v-text-field
              :label="$t('please_specify')"
              v-model="department_2"
              :error-messages="departmentErrors"
              solo/>
          </div>
        </div>

        <div v-if="errorMessage" class="welcome-error py-1">
          {{ errorMessage }}
        </div>

        <v-btn type="submit" class="mx-auto my-2">
          <v-icon>mdi-login</v-icon>
          {{ $t('set_up_my_account') }}
        </v-btn>
      </v-form>
    </div>
  </v-container>
</template>

<script>
import PasswordChangeForm from '@/components/shared/PasswordChangeForm'
import AuthUtil from '@/util/AuthUtil'
import ApiContainer from '@/orm/ApiContainer'
import ValidationMixin from '@/components/mixins/ValidationMixin'

import {required} from 'vuelidate/lib/validators'

export default {
  name: 'Welcome',
  components: {PasswordChangeForm},
  mixins: [
    ValidationMixin
  ],
  validations: {
    firstName: {
      required
    },
    lastName: {
      required
    },
    phone: {
      required
    },
    title: {
      required
    },
    department: {
      required
    }
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      phone: '',
      title: '',
      // The value of the department radio buttons
      department_1: '',
      // The value of the department free text field (If 'Other' is selected)
      department_2: '',
      // the password
      password: null,
      // Whether we are currently saving
      saving: false,
      // Contains any error messages to be displayed
      errorMessage: null
    }
  },
  computed: {
    /**
     * Return the department value. This is composed of two parts.
     * If the user picked 'other' it will be the value of the free text field (department_2),
     * otherwise it'll be the value in the options list (department_1)
     * @return {string}
     */
    department() {
      return this.department_1 === 'other' ? this.department_2 : this.department_1
    },
    firstNameErrors () {
      return this.validationErrors([{
        prop: this.$v.firstName,
        rule: 'required',
        message: this.$t('first_name_required')
      }])
    },
    lastNameErrors () {
      return this.validationErrors([{
        prop: this.$v.lastName,
        rule: 'required',
        message: this.$t('last_name_required')
      }])
    },
    phoneErrors () {
      return this.validationErrors([{
        prop: this.$v.phone,
        rule: 'required',
        message: this.$t('phone_number_required')
      }])
    },
    titleErrors () {
      return this.validationErrors([{
        prop: this.$v.title,
        rule: 'required',
        message: this.$t('job_title_required')
      }])
    },
    departmentErrors () {
      return this.validationErrors([{
        prop: this.$v.department,
        rule: 'required',
        message: this.$t('department_required')
      }])
    }
  },
  created() {
    // Fire off a quick API check to see if we're in maintenance mode. If we get a 503,
    // the interceptor will turn maintenance mode on.
    this.$httpAdapter({url: 'site/status'})
  },
  async mounted() {
    // Fetch user data by token to pre-populate fields
    const token = this.$route.query.token
    if (token) {
      try {
        const response = await this.$httpAdapter({
          url: 'auth/validate-welcome-token',
          params: { token }
        })

        if (response && response.data) {
          const user = ApiContainer.createRecord('User', response.data)
          const profile = user.getSetting('profile', {})

          // Pre-populate form fields with existing data
          if (profile.firstName) this.firstName = profile.firstName
          if (profile.lastName) this.lastName = profile.lastName
          if (profile.phone) this.phone = profile.phone
          if (profile.title) this.title = profile.title
          if (profile.department) {
            // Check if department is one of the predefined options
            if (this.departmentOptions().includes(profile.department)) {
              this.department_1 = profile.department
            } else {
              // It's a custom "other" value
              this.department_1 = 'other'
              this.department_2 = profile.department
            }
          }
        }
      } catch (error) {
        // If token validation fails, just show empty form
        // The error will be caught again on submit with a proper error message
        console.warn('Could not pre-populate user data:', error)
      }
    }
  },
  methods: {
    async onSubmit() {
      this.$v.$touch()
      const passwordValid = this.$refs['password-change-form'].validate()
      if (!passwordValid || this.$v.$invalid) return false

      const handleRegistrationError = (error) => {
        if (error) {
          const response = error.response
          if (response.status === 422) {
            if (response.data.message === 'Invalid Token') {
              this.errorMessage = this.$i18n.t('welcome_page_ui.token_invalid')
            } else {
              this.errorMessage = this.$i18n.t('welcome_page_ui.generic_error')
            }
          } else {
            this.errorMessage = this.$i18n.t('welcome_page_ui.generic_error')
          }
          this.saving = false
          throw (error)
        }
      }

      this.saving = true

      // Not sure if we need this or not, we shouldn't be registered with the socket so we shouldn't get any updates
      await this.$store.dispatch('userModule/ignoreNextUserUpdate')

      const response =
        await AuthUtil.resetPassword(this.password, this.$route.query.token)
          .catch(handleRegistrationError)

      const user = ApiContainer.createRecord('User', response.data)

      // Auth with new password, so we can change user settings
      await AuthUtil.auth(user.email, this.password)
        .catch(handleRegistrationError)

      // Save user settings with the data entered in the form
      // Note: Fields are pre-populated from validate-welcome-token if admin set them
      await user.updateSettings({
        profile: {
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone,
          title: this.title,
          department: this.department
        },
        homepage: '/routes'
      }).catch(handleRegistrationError)

      // User's registered, take them home
      this.$router.push('/')
    },
    departmentOptions() {
      return [
        'transportation',
        'warehouse',
        'quality',
        'other'
      ]
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/scss/colors.scss";

.container {
  padding: 0;
}

.welcome-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: rgb(137, 173, 207);
  background: linear-gradient(180deg, rgba(137, 173, 207, 1) 0%, rgba(133, 201, 222, 1) 100%);
}

.welcome {
  background: rgb(177, 225, 255);
  background: linear-gradient(180deg, rgba(177, 225, 255, 1) 0%, rgba(179, 245, 255, 1) 100%);
  width: 100%;
  max-width: 600px;
  border: 1px solid rgb(134, 154, 166);
  border-radius: 4px;
  color: rgb(112, 112, 112);
}

.greeting-text {
  text-align: center;
  margin: 1rem 0;
}

.welcome-input {
  width: 100%;
}

.welcome ::v-deep .Password {
  max-width: unset;
  margin: unset;
}

.welcome ::v-deep .password-strength {
  display: block;
  padding-bottom: 0.5rem;
}

.department {
  padding-top: 0.5rem;
}

.welcome-error {
  color: $red;
  font-size: .85rem;
  text-align: center;
}
</style>
